import moment from 'moment-timezone';
import { getAppNameByAppPath } from '@/utils/utility';
import {
  scoreLabel,
  APP_TILE_COLORS,
  fmtSeconds,
} from '../DashboardOverviewNew/DashboardOverviewNew.helpers';
import {
  KEY_HIGHLIGHTS_DATA,
  OVERALL_PRODUCTIVITY_DATA,
} from '../DashboardOverviewNew/data/dashboardOverviewNew.data';

const AVATAR_PALETTES = [
  { bg: '#EEF4FF', color: '#4F7EF8' },
  { bg: '#ECFDF3', color: '#22c55e' },
  { bg: '#FFF1F3', color: '#ef4444' },
  { bg: '#FFF6ED', color: '#f59e0b' },
  { bg: '#F4F3FF', color: '#7a5af8' },
];

function avatarInitials(name) {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Returns the absolute % change and its direction between two values.
// Used for "vs yesterday" trend indicators on every highlights card.
function pctDelta(current, previous) {
  if (!previous || previous === 0) return { pct: 0, up: true };
  const delta = ((current - previous) / previous) * 100;
  return { pct: Math.abs(Math.round(delta)), up: delta >= 0 };
}

// ── Widget builders ───────────────────────────────────────────────────────

export function buildOverallProductivity(today, yesterday, last7) {
  const score = today ? Math.round(today.productivityPercentage || 0) : 0;
  const delta = pctDelta(
    today?.productivityPercentage,
    yesterday?.productivityPercentage
  );
  // 7-day rolling average as the "Team Average" benchmark gauge
  const teamAvg =
    last7.length > 0
      ? Math.round(
          last7.reduce((s, d) => s + (d.productivityPercentage || 0), 0) /
            last7.length
        )
      : score;

  return {
    ...OVERALL_PRODUCTIVITY_DATA,
    score,
    label: scoreLabel(score),
    trendPct: delta.pct,
    trendUp: delta.up,
    teamAvg,
  };
}

export function buildKeyHighlights(today, yesterday, focusData) {
  const totalSec = today?.totalSec || 0;
  const idleSec = today?.idleSec || 0;
  const productiveSec = today?.productiveSec || 0;
  const presentCount = today?.presentCount || 0;
  const membersCount = today?.membersCount || 0;

  const attendancePct =
    membersCount > 0 ? Math.round((presentCount / membersCount) * 100) : 0;
  const prevAttendancePct =
    yesterday?.membersCount > 0
      ? Math.round(
          ((yesterday.presentCount || 0) / yesterday.membersCount) * 100
        )
      : 0;

  const activeTimeDelta = pctDelta(productiveSec, yesterday?.productiveSec);
  const idleTimeDelta = pctDelta(idleSec, yesterday?.idleSec);
  const attendanceDelta = pctDelta(attendancePct, prevAttendancePct);

  const rawActivePct = totalSec > 0 ? (productiveSec / totalSec) * 100 : 0;
  const rawIdlePct = totalSec > 0 ? (idleSec / totalSec) * 100 : 0;
  const scale =
    rawActivePct + rawIdlePct > 100 ? 100 / (rawActivePct + rawIdlePct) : 1;
  const activePct = Math.round(rawActivePct * scale);
  const idlePct = Math.round(rawIdlePct * scale);

  return KEY_HIGHLIGHTS_DATA.map((item) => {
    switch (item.id) {
      case 'active-time':
        return {
          ...item,
          value: productiveSec > 0 ? fmtSeconds(productiveSec) : '—',
          trendPct: activeTimeDelta.pct,
          trendUp: activeTimeDelta.up,
          sublabel: totalSec > 0 ? `${activePct}%` : '—',
          sublabelSuffix: 'of tracked time',
        };
      case 'idle-time':
        return {
          ...item,
          value: idleSec > 0 ? fmtSeconds(idleSec) : '—',
          trendPct: idleTimeDelta.pct,
          trendUp: idleTimeDelta.up,
          sublabel: totalSec > 0 ? `${idlePct}%` : '—',
          sublabelSuffix: 'of tracked time',
        };
      case 'attendance':
        return {
          ...item,
          value: membersCount > 0 ? `${attendancePct}%` : '—',
          trendPct: attendanceDelta.pct,
          trendUp: attendanceDelta.up,
          sublabel: `${presentCount}/${membersCount}`,
          sublabelSuffix: 'present',
        };
      case 'focus-time':
        return {
          ...item,
          value: focusData ? focusData.segments[0].time : '—',
          trendPct: 0,
          trendUp: true,
          sublabel: focusData ? `${focusData.focusPct}%` : '—',
          sublabelSuffix: focusData ? 'of tracked time' : '',
        };
      case 'alerts':
        return {
          ...item,
          value: today?.alertsCount != null ? String(today.alertsCount) : '—',
          trendPct: 0,
          trendUp: false,
          sublabel: '',
          sublabelSuffix: '',
        };
      default:
        return item;
    }
  });
}

// Builds 24 hourly data points from the UTC-keyed hourly maps.
// Each UTC hour is converted to a local-time label for the x-axis.
export function buildProductivityOverTime(today, last7, timezone) {
  if (!today?.hourlyTotalSec) return [];
  const tz = timezone || moment.tz.guess();
  const { hourlyTotalSec = {}, hourlyProductiveSec = {}, dateId = '' } = today;

  const teamAvg =
    last7?.length > 0
      ? Math.round(
          last7.reduce((s, d) => s + (d.productivityPercentage || 0), 0) /
            last7.length
        )
      : 0;

  return Array.from({ length: 24 }, (_, utcH) => {
    const totalSec = Number(hourlyTotalSec[utcH]) || 0;
    const productiveSec = Number(hourlyProductiveSec[utcH]) || 0;
    const score =
      totalSec > 0 ? Math.round((productiveSec / totalSec) * 100) : 0;
    const label = moment
      .utc(`${dateId}T${String(utcH).padStart(2, '0')}:00:00Z`)
      .tz(tz)
      .format('h A');
    return { time: label, score, teamAvg };
  });
}

// 6 evenly-spaced ticks across the 24-hour window
export function buildChartTicks(today, timezone) {
  const data = buildProductivityOverTime(today, null, timezone);
  if (!data.length) return undefined;
  return data.filter((_, i) => i % 4 === 0).map((d) => d.time);
}

export function buildActivityBreakdown(today) {
  if (!today?.totalSec) return null;
  const { totalSec, productiveSec = 0, idleSec = 0, offlineSec = 0 } = today;

  const spanSec = totalSec + offlineSec;
  const activePct = Math.round((productiveSec / spanSec) * 100);
  const idlePct = Math.round((idleSec / spanSec) * 100);
  const offlinePct = Math.max(0, 100 - activePct - idlePct);

  return {
    totalTime: fmtSeconds(productiveSec),
    label: 'Total Active Time',
    segments: [
      { name: 'Active', value: activePct, color: '#22c55e' },
      { name: 'Idle', value: idlePct, color: '#f59e0b' },
      { name: 'Offline', value: offlinePct, color: '#9CA3AF' },
    ],
  };
}

export function buildTopApplications(appUsage, appNameMapping) {
  if (!appUsage || !Object.keys(appUsage).length) return null;
  const totalSeconds = Object.values(appUsage).reduce((s, v) => s + v, 0);
  if (totalSeconds === 0) return null;

  const sorted = Object.entries(appUsage).sort(([, a], [, b]) => b - a);
  const top = sorted.slice(0, 4).map(([path, secs], i) => ({
    name: getAppNameByAppPath(path, appNameMapping),
    time: fmtSeconds(secs),
    pct: ((secs / totalSeconds) * 100).toFixed(2),
    color: APP_TILE_COLORS[i],
  }));

  const restSec = sorted.slice(4).reduce((s, [, v]) => s + v, 0);
  if (restSec > 0) {
    top.push({
      name: 'Others',
      time: fmtSeconds(restSec),
      pct: ((restSec / totalSeconds) * 100).toFixed(2),
      color: APP_TILE_COLORS[4],
    });
  }

  return top;
}

export function buildAttendanceSummary(today) {
  if (!today?.membersCount) return null;
  const { presentCount = 0, membersCount, lateCount = 0 } = today;
  const absent = membersCount - presentCount;
  const onTime = presentCount - lateCount;
  return {
    overall: Math.round((presentCount / membersCount) * 100),
    presentCount,
    totalCount: membersCount,
    segments: [
      { name: 'Present', value: onTime, color: '#22c55e' },
      { name: 'Absent', value: absent, color: '#ef4444' },
      { name: 'Late', value: lateCount, color: '#f59e0b' },
    ],
  };
}

// Maps UTC-keyed hourlyTotalSec across last 7 calendar days into the
// 7×13 intensity grid the ActivityHeatmap component expects.
// Days with no consolidated doc render as all-zero (white) cells.
export function buildActivityHeatmap(last7, selectedDate, timezone) {
  const tz = timezone || moment.tz.guess();
  const docByDate = new Map((last7 || []).map((d) => [d.dateId, d]));

  // Always produce exactly 7 consecutive calendar rows ending on selectedDate
  const dates = Array.from({ length: 7 }, (_, i) =>
    moment(selectedDate)
      .subtract(6 - i, 'days')
      .format('YYYY-MM-DD')
  );

  // Build raw slot totals (24 one-hour buckets) per day
  const rawRows = dates.map((dateId) => {
    const hourlyTotalSec = docByDate.get(dateId)?.hourlyTotalSec || {};
    const slots = Array(24).fill(0);

    for (const [utcHStr, sec] of Object.entries(hourlyTotalSec)) {
      const localH = moment
        .utc(`${dateId}T${String(Number(utcHStr)).padStart(2, '0')}:00:00Z`)
        .tz(tz)
        .hour();
      slots[localH] += Number(sec) || 0;
    }

    return { day: moment(dateId).format('ddd'), slots };
  });

  // Normalize against the busiest slot across the whole week (0.0–1.0)
  const maxSec = Math.max(...rawRows.flatMap((r) => r.slots), 1);

  return rawRows.map(({ day, slots }) => ({
    day,
    slots: slots.map((s) => s / maxSec),
  }));
}

export function buildTeamPerformanceRows(
  top5Performers,
  memberDetails,
  yesterday,
  last7
) {
  if (!top5Performers?.length) return null;

  // Build lookup: uid → productivityPct for each of the 7 days
  const last7ByUid = (last7 || []).map((doc) => {
    const map = {};
    (doc.top5Performers || []).forEach((p) => {
      map[p.userUID] = Math.round(p.productivityPct || 0);
    });
    return map;
  });

  // Build lookup: uid → productivityPct for yesterday
  const yesterdayByUid = {};
  (yesterday?.top5Performers || []).forEach((p) => {
    yesterdayByUid[p.userUID] = Math.round(p.productivityPct || 0);
  });

  return top5Performers.map((perf, index) => {
    const member = memberDetails?.[perf.userUID] || {};
    const palette = AVATAR_PALETTES[index % AVATAR_PALETTES.length];
    const score = Math.round(perf.productivityPct || 0);

    const prevScore = yesterdayByUid[perf.userUID] ?? 0;
    const scoreTrend = Math.abs(score - prevScore);
    const scoreTrendUp = score >= prevScore;

    // 7-point sparkline: one point per day, 0 when member wasn't in top5 that day
    const sparkline = last7ByUid.map((dayMap) => dayMap[perf.userUID] ?? 0);

    return {
      id: perf.userUID,
      name: member.userName || perf.userUID,
      role: member.userType || member.role || '—',
      avatar: avatarInitials(member.userName || perf.userUID),
      avatarBg: palette.bg,
      avatarColor: palette.color,
      productivityScore: score,
      scoreTrend,
      scoreTrendUp,
      activeTime: fmtSeconds(perf.productiveSec || 0),
      idleTime: fmtSeconds(perf.idleSec || 0),
      focusTime: null,
      attendance: perf.present ? (perf.isLate ? 'Late' : '100%') : '—',
      attendanceStatus: perf.present
        ? perf.isLate
          ? 'Late'
          : 'Present'
        : 'Absent',
      sparkline,
      sparkColor: score >= 70 ? '#22c55e' : '#f59e0b',
    };
  });
}
